// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Building2, AlertCircle, Search, Plus } from 'lucide-react'
import { listOrgs, ApiError } from '@/lib/api'
import type { OrgSummary, OrgStatus } from '@/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import AccessDenied from '@/components/AccessDenied'

type PageState = 'loading' | 'error' | 'forbidden' | 'empty' | 'list'

// Map OrgStatus → badge variant
function orgStatusVariant(status: OrgStatus) {
  switch (status) {
    case 'active':    return 'active' as const
    case 'suspended': return 'suspended' as const
    case 'archived':  return 'archived' as const
  }
}

function SkeletonRow() {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <div className="h-4 w-36 rounded shimmer" />
          <div className="h-3 w-20 rounded shimmer" />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="h-5 w-20 rounded-full shimmer" />
      </td>
    </tr>
  )
}

export default function OrgsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [orgs, setOrgs] = useState<OrgSummary[]>([])
  const [pageState, setPageState] = useState<PageState>('loading')
  const [errorMsg, setErrorMsg] = useState('')
  const [search, setSearch] = useState('')

  const fetchOrgs = useCallback(async () => {
    setPageState('loading')
    setErrorMsg('')
    try {
      const data = await listOrgs()
      setOrgs(data)
      setPageState(data.length === 0 ? 'empty' : 'list')
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPageState('forbidden')
      } else {
        setErrorMsg(err instanceof Error ? err.message : String(err))
        setPageState('error')
      }
    }
  }, [])

  useEffect(() => { void fetchOrgs() }, [fetchOrgs])

  // Derive counts from loaded data (no extra API calls)
  const activeCount = orgs.filter(o => o.status === 'active').length
  const suspendedCount = orgs.filter(o => o.status === 'suspended').length

  // Client-side search filter
  const filtered = orgs.filter(o => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      o.displayName.toLowerCase().includes(q) ||
      o.slug.toLowerCase().includes(q)
    )
  })

  if (pageState === 'forbidden') {
    return <AccessDenied />
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#012031] via-[#033C85] to-[#006EF9]">
        <div className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full bg-[#02C7C3]/20 blur-3xl pointer-events-none" />
        <div className="relative z-10 px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0">
              <Building2 className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white leading-tight">{t('orgs.title')}</h1>
              {pageState === 'list' && (
                <p className="text-white/65 text-sm mt-1">
                  {t('orgs.summary', {
                    total: orgs.length,
                    active: activeCount,
                    suspended: suspendedCount,
                  })}
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={() => navigate('/orgs/new')}
            className="w-full sm:w-auto bg-white text-[#033C85] hover:bg-white/90 border-0 font-semibold shadow-lg"
          >
            <Plus className="h-4 w-4" />
            {t('orgs.onboardOrg')}
          </Button>
        </div>
      </div>

      {/* Loading skeleton */}
      {pageState === 'loading' && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <table className="w-full" role="table">
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
      )}

      {/* Error state */}
      {pageState === 'error' && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-destructive" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('orgs.loadError')}</p>
          {errorMsg && (
            <p className="text-xs text-muted-foreground max-w-xs">{errorMsg}</p>
          )}
          <Button variant="outline" size="sm" onClick={() => void fetchOrgs()} className="mt-1">
            {t('orgs.retry')}
          </Button>
        </div>
      )}

      {/* Empty state */}
      {pageState === 'empty' && (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-secondary flex items-center justify-center">
            <Building2 className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">{t('orgs.empty')}</p>
          <p className="text-xs text-muted-foreground max-w-xs">{t('orgs.emptyBody')}</p>
          <Button size="sm" onClick={() => navigate('/orgs/new')} className="mt-1">
            {t('orgs.onboardOrg')}
          </Button>
        </div>
      )}

      {/* List */}
      {pageState === 'list' && (
        <div className="flex flex-col gap-4">
          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              className="pl-10"
              placeholder={t('orgs.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <table className="w-full" role="table">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('orgs.colOrganization')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('orgs.colStatus')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      {t('orgs.noResults')}
                    </td>
                  </tr>
                ) : (
                  filtered.map(org => (
                    <tr
                      key={org.id}
                      className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/orgs/${org.id}`)}
                    >
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-foreground">{org.displayName}</p>
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">{org.slug}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={orgStatusVariant(org.status)}>
                          {t(`orgs.status.${org.status}`)}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
